import * as React from 'react';
import { createContext, useContext, useCallback, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from './button';

interface ConfirmOptions {
  title: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'default' | 'destructive';
}

interface ConfirmContextType {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextType | null>(null);

export function useConfirm() {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return context;
}

interface ConfirmState extends ConfirmOptions {
  resolve: (value: boolean) => void;
}

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<ConfirmState | null>(null);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setState({ ...options, resolve });
    });
  }, []);

  const handleConfirm = () => {
    state?.resolve(true);
    setState(null);
  };

  const handleCancel = () => {
    state?.resolve(false);
    setState(null);
  };

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {state && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center">
          <div
            className="fixed inset-0 bg-surface-backdrop animate-in fade-in duration-200"
            onClick={handleCancel}
          />
          <div className="relative bg-surface-base border border-border-default rounded-lg shadow-xl max-w-md w-full mx-4 animate-in zoom-in-95 duration-200">
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div
                  className={`
                    flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center
                    ${state.variant === 'destructive' ? 'bg-signal-red-dim' : 'bg-signal-amber-dim'}
                  `}
                >
                  <AlertTriangle
                    className={`w-5 h-5 ${
                      state.variant === 'destructive'
                        ? 'text-signal-red'
                        : 'text-signal-amber'
                    }`}
                  />
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-lg font-semibold text-text-primary">
                    {state.title}
                  </h3>
                  {state.description && (
                    <p className="mt-2 text-sm text-text-secondary">
                      {state.description}
                    </p>
                  )}
                </div>
                <button
                  onClick={handleCancel}
                  className="flex-shrink-0 p-1 rounded hover:bg-surface-overlay transition-colors"
                >
                  <X className="w-5 h-5 text-text-tertiary" />
                </button>
              </div>
            </div>
            <div className="flex gap-3 px-6 pb-6">
              <Button
                variant="outline"
                className="flex-1"
                onClick={handleCancel}
              >
                {state.cancelText || 'Cancel'}
              </Button>
              <Button
                variant={
                  state.variant === 'destructive' ? 'destructive' : 'default'
                }
                className="flex-1"
                onClick={handleConfirm}
              >
                {state.confirmText || 'Confirm'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}
